import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logActivity } from '../../lib/activity';
import { QuotationNameStep } from './steps/QuotationNameStep';
import { VolumeStep } from './steps/VolumeStep';
import { FeeStructureStep } from './steps/FeeStructureStep';
import { BankFeeStep } from './steps/BankFeeStep';
import { StepNavigation } from './components/StepNavigation';

interface QuotationFormProps {
  onClose: () => void;
  initialData?: {
    id: string;
    name: string;
    bank_id: string;
    estimated_payments: number;
    estimated_payouts: number;
    average_amount: number;
    payment_fixed_fee: number;
    payment_percentage_fee: number;
    payment_max_cap: number;
    payout_fixed_fee: number;
    payout_percentage_fee: number;
    payout_max_cap: number;
  };
}

import { Bank, BankFee, Merchant, MerchantFee } from '../../types';

export function QuotationForm({ onClose, initialData }: QuotationFormProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [, setLoading] = useState(false);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [merchantFees, setMerchantFees] = useState<MerchantFee[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [bankFees, setBankFees] = useState<BankFee[]>([]);
  const [loadingFees, setLoadingFees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    volume: {
      payments: {
        monthlyVolume: initialData?.estimated_payments || 0,
        averageAmount: initialData?.average_amount || 0
      },
      payouts: {
        monthlyVolume: initialData?.estimated_payouts || 0,
        averageAmount: initialData?.average_amount || 0
      }
    },
    merchantFees: {
      merchantId: initialData?.merchant_id || '',
      useCustomFees: true,
      payments: {
        approved: {
          fixedFee: initialData?.payment_approved_fixed_fee || 0,
          percentageFee: initialData?.payment_approved_percentage_fee || 0,
          maxCap: initialData?.payment_approved_max_cap || 0
        },
        declined: {
          fixedFee: initialData?.payment_declined_fixed_fee || 0,
          percentageFee: initialData?.payment_declined_percentage_fee || 0,
          maxCap: initialData?.payment_declined_max_cap || 0
        },
        returned: {
          fixedFee: initialData?.payment_returned_fixed_fee || 0,
          percentageFee: initialData?.payment_returned_percentage_fee || 0,
          maxCap: initialData?.payment_returned_max_cap || 0
        }
      },
      payouts: {
        approved: {
          fixedFee: initialData?.payout_approved_fixed_fee || 0,
          percentageFee: initialData?.payout_approved_percentage_fee || 0,
          maxCap: initialData?.payout_approved_max_cap || 0
        },
        declined: {
          fixedFee: initialData?.payout_declined_fixed_fee || 0,
          percentageFee: initialData?.payout_declined_percentage_fee || 0,
          maxCap: initialData?.payout_declined_max_cap || 0
        },
        returned: {
          fixedFee: initialData?.payout_returned_fixed_fee || 0,
          percentageFee: initialData?.payout_returned_percentage_fee || 0,
          maxCap: initialData?.payout_returned_max_cap || 0
        }
      }
    },
    bankFees: {
      bankId: initialData?.bank_id || '',
      useCustomFees: true,
      payments: {
        approved: {
          fixedFee: 0,
          percentageFee: 0,
          maxCap: 0
        },
        declined: {
          fixedFee: 0,
          percentageFee: 0,
          maxCap: 0
        },
        returned: {
          fixedFee: 0,
          percentageFee: 0,
          maxCap: 0
        }
      },
      payouts: {
        approved: {
          fixedFee: 0,
          percentageFee: 0,
          maxCap: 0
        },
        declined: {
          fixedFee: 0,
          percentageFee: 0,
          maxCap: 0
        },
        returned: {
          fixedFee: 0,
          percentageFee: 0,
          maxCap: 0
        }
      }
    }
  });

  useEffect(() => {
    loadMerchants();
    loadBanks();
  }, []);

  const loadBanks = async () => {
    try {
      const [banksResult, feesResult] = await Promise.all([
        supabase.from('banks').select('*').order('name'),
        supabase.from('bank_fees').select('*')
      ]);

      if (banksResult.error) throw banksResult.error;
      if (feesResult.error) throw feesResult.error;

      setBanks(banksResult.data || []);
      setBankFees(feesResult.data || []);
    } catch (err) {
      console.error('Error loading banks:', err);
      setError(err instanceof Error ? err.message : 'Failed to load banks');
    }
  };

  const loadMerchants = async () => {
    try {
      setLoading(true);
      const [merchantsResult, feesResult] = await Promise.all([
        supabase.from('merchants').select('*').order('name'),
        supabase.from('merchant_fees').select('*')
      ]);

      if (merchantsResult.error) throw merchantsResult.error;
      if (feesResult.error) throw feesResult.error;

      setMerchants(merchantsResult.data || []);
      setMerchantFees(feesResult.data || []);
    } catch (err) {
      console.error('Error loading merchants:', err);
      setError(err instanceof Error ? err.message : 'Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const handleMerchantChange = (merchantId: string) => {
    setLoadingFees(true);
    if (!merchantId) {
      setFormData(prev => ({
        ...prev,
        merchantFees: {
          ...prev.merchantFees,
          merchantId: '',
          useCustomFees: true,
          payments: {
            approved: { fixedFee: 0, percentageFee: 0, maxCap: 0 },
            declined: { fixedFee: 0, percentageFee: 0, maxCap: 0 },
            returned: { fixedFee: 0, percentageFee: 0, maxCap: 0 }
          },
          payouts: {
            approved: { fixedFee: 0, percentageFee: 0, maxCap: 0 },
            declined: { fixedFee: 0, percentageFee: 0, maxCap: 0 },
            returned: { fixedFee: 0, percentageFee: 0, maxCap: 0 }
          }
        }
      }));
      setLoadingFees(false);
      return;
    }

    const fees = merchantFees.filter(f => f.merchant_id === merchantId);
    setFormData(prev => ({
      ...prev,
      merchantFees: {
        ...prev.merchantFees,
        merchantId,
        useCustomFees: false,
        payments: {
          approved: {
            fixedFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'approved')?.per_transaction_fee || 0,
            percentageFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'approved')?.rate_fee_percentage || 0,
            maxCap: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'approved')?.max_fee_cap || 0
          },
          declined: {
            fixedFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'declined')?.per_transaction_fee || 0,
            percentageFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'declined')?.rate_fee_percentage || 0,
            maxCap: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'declined')?.max_fee_cap || 0
          },
          returned: {
            fixedFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'returned')?.per_transaction_fee || 0,
            percentageFee: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'returned')?.rate_fee_percentage || 0,
            maxCap: fees.find(f => f.payment_method === 'etransfer_request_money' && f.status === 'returned')?.max_fee_cap || 0
          }
        },
        payouts: {
          approved: {
            fixedFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'approved')?.per_transaction_fee || 0,
            percentageFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'approved')?.rate_fee_percentage || 0,
            maxCap: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'approved')?.max_fee_cap || 0
          },
          declined: {
            fixedFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'declined')?.per_transaction_fee || 0,
            percentageFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'declined')?.rate_fee_percentage || 0,
            maxCap: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'declined')?.max_fee_cap || 0
          },
          returned: {
            fixedFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'returned')?.per_transaction_fee || 0,
            percentageFee: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'returned')?.rate_fee_percentage || 0,
            maxCap: fees.find(f => f.payment_method === 'etransfer_send_money' && f.status === 'returned')?.max_fee_cap || 0
          }
        }
      }
    }));
    setLoadingFees(false);
  };

  const handleBankChange = (bankId: string) => {
    setLoadingFees(true);
    if (!bankId) {
      setFormData(prev => ({
        ...prev,
        bankFees: {
          ...prev.bankFees,
          bankId: ''
        }
      }));
      setLoadingFees(false);
      return;
    }

    setFormData(prev => ({
      ...prev,
      bankFees: {
        ...prev.bankFees,
        bankId
      }
    }));
    setLoadingFees(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If not on the last step, just move to next step
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      return;
    }
    
    // Only proceed with submission on the last step
    setIsSubmitting(true);
    setError(null);

    try {
      const quotationData: any = {
        name: formData.name,
        merchant_id: formData.merchantFees.merchantId || null,
        bank_id: formData.bankFees.bankId || null,
        estimated_payments: formData.volume.payments.monthlyVolume,
        estimated_payouts: formData.volume.payouts.monthlyVolume,
        average_amount: formData.volume.payments.averageAmount,
        created_by: (await supabase.auth.getUser()).data.user?.id,
        payment_approved_fixed_fee: formData.merchantFees.payments.approved.fixedFee,
        payment_approved_percentage_fee: formData.merchantFees.payments.approved.percentageFee,
        payment_approved_max_cap: formData.merchantFees.payments.approved.maxCap,
        payment_declined_fixed_fee: formData.merchantFees.payments.declined.fixedFee,
        payment_declined_percentage_fee: formData.merchantFees.payments.declined.percentageFee,
        payment_declined_max_cap: formData.merchantFees.payments.declined.maxCap,
        payment_returned_fixed_fee: formData.merchantFees.payments.returned.fixedFee,
        payment_returned_percentage_fee: formData.merchantFees.payments.returned.percentageFee,
        payment_returned_max_cap: formData.merchantFees.payments.returned.maxCap,
        payout_approved_fixed_fee: formData.merchantFees.payouts.approved.fixedFee,
        payout_approved_percentage_fee: formData.merchantFees.payouts.approved.percentageFee,
        payout_approved_max_cap: formData.merchantFees.payouts.approved.maxCap,
        payout_declined_fixed_fee: formData.merchantFees.payouts.declined.fixedFee,
        payout_declined_percentage_fee: formData.merchantFees.payouts.declined.percentageFee,
        payout_declined_max_cap: formData.merchantFees.payouts.declined.maxCap,
        payout_returned_fixed_fee: formData.merchantFees.payouts.returned.fixedFee,
        payout_returned_percentage_fee: formData.merchantFees.payouts.returned.percentageFee,
        payout_returned_max_cap: formData.merchantFees.payouts.returned.maxCap
      };

      if (initialData?.id) {
        const { error: updateError } = await supabase
          .from('quotations')
          .update(quotationData)
          .eq('id', initialData.id);

        if (updateError) throw updateError;
        
        // Log activity for update
        await logActivity(
          'Updated',
          'quotation',
          initialData.id,
          formData.name
        );
      } else {
        const { data: newQuote, error: insertError } = await supabase
          .from('quotations')
          .insert(quotationData)
          .select()
          .single();

        if (insertError) throw insertError;
        
        // Log activity for new quote
        if (newQuote) {
          await logActivity(
            'Created',
            'quotation',
            newQuote.id,
            formData.name
          );
        }
      }

      onClose();
      navigate('/quotations');
    } catch (err) {
      console.error('Error saving quotation:', err);
      setError(err instanceof Error ? err.message : 'Failed to save quotation');
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {initialData ? 'Edit' : 'New'} Quotation
            </h2>
            <p className="mt-0.5 text-[13px] text-slate-500">
              Step {currentStep} of 4
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            {currentStep === 1 && (
              <QuotationNameStep
                name={formData.name}
                onChange={(name) => setFormData(prev => ({ ...prev, name }))}
              />
            )}
            {currentStep === 2 && (
              <VolumeStep
                volume={formData.volume}
                onChange={(volume) => setFormData(prev => ({ ...prev, volume }))}
              />
            )}
            {currentStep === 3 && (
              <FeeStructureStep
                merchants={merchants}
                merchantFees={merchantFees}
                loading={loadingFees}
                fees={formData.merchantFees}
                onMerchantChange={handleMerchantChange}
                onFeesChange={(fees) => setFormData(prev => ({ ...prev, merchantFees: fees }))}
              />
            )}
            {currentStep === 4 && (
              <BankFeeStep
                banks={banks}
                bankFees={bankFees}
                loading={loadingFees}
                fees={formData.bankFees}
                onBankChange={handleBankChange}
                onFeesChange={(fees) => setFormData(prev => ({ ...prev, bankFees: fees }))}
              />
            )}
          </div>

          {error && (
            <div className="mx-6 mb-4 p-3 rounded-xl bg-red-50 border border-red-100">
              <p className="text-[13px] text-red-600">{error}</p>
            </div>
          )}

          <StepNavigation
            currentStep={currentStep}
            totalSteps={4}
            isSubmitting={isSubmitting}
            onBack={handleBack}
            onCancel={onClose}
          />
        </form>
      </div>
    </div>
  );
}